import {useMemo, useSyncExternalStore} from "react";
import {Type} from "typebox";
import {readSessionState, writeSessionState} from "../../../hooks/sessionState";
import {BasisItem, DateRange, OeAssessment, Scenario} from "./model";
import {SCENARIOS} from "./scenarios";
import {TestProgress, TestResult, seedScenario, useEngineState} from "./testLifecycle";
import {testIdFor} from "./testLink";

// The demo surfaces sit in different route trees (a control's tabs, and the plan's pages), so the
// active scenario lives outside React rather than in a provider that would have to wrap both. It is
// held in session storage as well: a full page load — following a link straight to the dependency
// map, say — would otherwise drop back to the first scenario, which has no dependencies to show.

const STORAGE_KEY = "oeDemoScenario";
const ScenarioIdSchema = Type.String();

/**
 * `?scenario=s6` pins a tab to one scenario. It is read once, at load, so a walkthrough can be a set
 * of ordinary browser tabs opened beforehand rather than a control anybody has to touch on screen.
 */
const scenarioFromUrl = (): string | undefined => {
  const id = new URLSearchParams(window.location.search).get("scenario") ?? undefined;
  return id && SCENARIOS.some(s => s.id === id) ? id : undefined;
};

let activeId = scenarioFromUrl() ?? readSessionState(STORAGE_KEY, ScenarioIdSchema, SCENARIOS[0].id);
// Persisted immediately, so it survives the navigations that drop the query string.
writeSessionState(STORAGE_KEY, ScenarioIdSchema, activeId);
const listeners = new Set<() => void>();

const subscribe = (listener: () => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};

const scenarioFor = (id: string): Scenario => SCENARIOS.find(s => s.id === id) ?? SCENARIOS[0];

// The engine's state belongs to the scenario, so it is loaded with it rather than left to the page.
seedScenario(scenarioFor(activeId));

export const setActiveScenario = (id: string) => {
  activeId = id;
  writeSessionState(STORAGE_KEY, ScenarioIdSchema, id);
  seedScenario(scenarioFor(id));
  for (const listener of listeners) listener();
};

export const useActiveScenarioId = (): string => useSyncExternalStore(subscribe, () => activeId);

/**
 * Put the engine back to what the scenario declares, discarding every test started or concluded
 * since. Seeded from the fixture rather than the resolved scenario, whose `underway` is the live
 * state and would seed itself straight back in.
 */
export const resetScenario = () => seedScenario(scenarioFor(activeId));

// A plan created from the wizard replaces the scenario's own period and basis for that control and
// scope. Unlike the scenario id this is kept in memory only: it is made by an action in this tab, so
// there is no link-followed-cold case for it to survive.

/** The period travels with the basis: the spans were laid out against it. */
export type CreatedPlan = {period: DateRange; basis: BasisItem[]};

// Replaced rather than mutated on write, so the map itself is a snapshot React can compare.
let plans: ReadonlyMap<string, CreatedPlan> = new Map();

const planKey = (scenarioId: string, control: string, scope: string) => `${scenarioId}:${control}:${scope}`;

export const createPlan = (scenarioId: string, control: string, scope: string, plan: CreatedPlan) => {
  plans = new Map(plans).set(planKey(scenarioId, control, scope), plan);
  for (const listener of listeners) listener();
};

/** A planned item whose test has finished, rewritten as the obtained evidence that test produced. */
const asObtained = (item: BasisItem, result: TestResult): BasisItem => {
  if (item.status !== "planned" || item.evidence.kind !== "test") return item;
  return {
    id: item.id,
    span: item.span,
    conditions: item.conditions,
    status: "obtained",
    evidence: {
      kind: "test",
      label: item.evidence.procedure?.name ?? "Test",
      itemsTested: result.itemsTested,
      exceptions: result.exceptions,
      outcome: result.outcome,
      conclusion: result.conclusion,
    },
  };
};

const resolveAssessment = (
  assessment: OeAssessment,
  scenarioId: string,
  progress: ReadonlyMap<string, TestProgress>,
  results: ReadonlyMap<string, TestResult>,
): OeAssessment => {
  const planned = plans.get(planKey(scenarioId, assessment.control, assessment.scope));
  const base = planned ? {...assessment, ...planned} : assessment;

  const underway: Record<string, TestProgress> = {};
  const basis = base.basis.map(item => {
    const testId = testIdFor(scenarioId, assessment.control, assessment.scope, item.id);
    const result = results.get(testId);
    if (result) return asObtained(item, result);
    const running = progress.get(testId);
    if (running) underway[item.id] = running;
    return item;
  });

  return {...base, basis, underway};
};

/**
 * The active scenario as it currently stands, with everything done since it loaded folded in: plans
 * made in the wizard, tests started, tests concluded.
 *
 * Resolved here rather than at each surface because a basis item's status is read all over — coverage
 * maths, validity, the card, the conditions label — and every one of them has to agree about which
 * world it is looking at.
 */
export const useScenario = (): Scenario => {
  const id = useActiveScenarioId();
  const planSnapshot = useSyncExternalStore(subscribe, () => plans);
  const {progress, results} = useEngineState();

  return useMemo(() => {
    const scenario = scenarioFor(id);
    // Every map is replaced rather than mutated on write, so this only reruns on a real change.
    if (planSnapshot.size === 0 && progress.size === 0 && results.size === 0) return scenario;
    return {...scenario, oe: scenario.oe.map(a => resolveAssessment(a, id, progress, results))};
  }, [id, planSnapshot, progress, results]);
};
